import {
  EDatasourceType,
  EProjectType,
  IActionDefinitionDto,
  IActionFolder,
  IDatabaseTableModel,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
// Not re-exported from the package root; the deep path is how the Sources tree reaches its peers too.
import { ITableFolder } from "@kuika/kuika-cl-model/src/models/table-folder";
import { isSqlActionDatasourceType } from "../../../shared/utility/action-definition-filter";
import { NAME_COLLATOR } from "../../../shared/utility/name-collator";
import {
  lookupModification,
  selectRecentlyModified,
  type IRecentlyModifiedEntry,
  type ModificationIndex
} from "../../../shared/utility/recently-modified";
import { getTableFolderMemberId, groupTablesBySchema, hasSessionVariablesRow, hasTablesGroup } from "./datasource-tree";

/**
 * Everything the Datasources landing page derives, with no React in it: the connector
 * catalog the create modal offers, and the datasource → tables/actions tree the list shows.
 *
 * The catalog is the one that used to live inline in `create-connection.tsx`'s three-column
 * screen. It is data here so the modal can filter it by category and by project type without
 * re-deriving which types exist, and so the gates travel with the entries they gate.
 */

/** Which create card offers this connector. */
export type DatasourceCreateCard = "database" | "api" | "function";

export interface IDatasourceConnector {
  type: EDatasourceType;
  card: DatasourceCreateCard;
  /** Heading it sits under inside the modal, when a card carries more than one group. */
  section: string;
  /** Asset name in `public/images/datasource-icons/`. */
  iconName: string;
  label: string;
  description: string;
  /**
   * Pills beside the label. Today only the C# option carries any: the platform versions it
   * compiles against, which mirror `KCompiler.ParseOptions` and the TFM `CsProjectFileGenerator`
   * emits — a badge that disagrees with the compiler is worse than no badge.
   */
  badges?: string[];
  /**
   * Subscription asset checked with `KMainFunctions.isDomainHasAccess` before the type can be
   * picked. Absent means "no asset gates this" — the scripting runtimes are deliberately ungated,
   * because no plan grants an asset for them and gating would block everyone.
   */
  accessName?: string;
  /** Present when the connector only exists for one project type. */
  projectType?: EProjectType;
}

/**
 * Order matters: it is the order the modal lists them in, and it is the order the old screen
 * used. AI Actions / AI Agents / Vision Actions are deliberately absent — they are created
 * from AI Functions, and the old screen had them commented out for the same reason.
 */
export const DATASOURCE_CONNECTORS: IDatasourceConnector[] = [
  {
    type: EDatasourceType.MsSql,
    card: "database",
    section: "Databases",
    iconName: "database",
    label: "MS SQL Server",
    description: "Microsoft SQL Server database"
  },
  {
    type: EDatasourceType.Postgre,
    card: "database",
    section: "Databases",
    iconName: "postgre",
    label: "PostgreSQL",
    description: "PostgreSQL database"
  },
  {
    type: EDatasourceType.Oracle,
    card: "database",
    section: "Databases",
    iconName: "oracle",
    label: "Oracle",
    description: "Oracle database"
  },
  {
    type: EDatasourceType.RestApi,
    card: "api",
    section: "APIs & Services",
    iconName: "swagger",
    label: "Swagger API",
    description: "OpenAPI/Swagger specification",
    accessName: "Swagger API"
  },
  {
    type: EDatasourceType.RestApiWOSwagger,
    card: "api",
    section: "APIs & Services",
    iconName: "cloud-cog",
    label: "REST API",
    description: "RESTful web services",
    accessName: "REST API"
  },
  {
    type: EDatasourceType.OData,
    card: "api",
    section: "APIs & Services",
    iconName: "odata",
    label: "OData",
    description: "Open Data Protocol",
    accessName: "OData"
  },
  {
    type: EDatasourceType.SAP,
    card: "api",
    section: "APIs & Services",
    iconName: "sap",
    label: "SAP",
    description: "SAP RFC/Connector",
    accessName: "SAP"
  },
  {
    type: EDatasourceType.CSharpFunctions,
    card: "function",
    section: "Development",
    iconName: "hexagon",
    label: "C# Functions",
    description: "Custom C# code functions",
    accessName: "C#",
    badges: [".NET 10", "C# 14"]
  },
  {
    type: EDatasourceType.JavaScriptAction,
    card: "function",
    section: "Development",
    iconName: "typescript",
    label: "TypeScript",
    description: "Custom TypeScript functions"
  },
  {
    type: EDatasourceType.PythonAction,
    card: "function",
    section: "Development",
    iconName: "python",
    label: "Python",
    description: "Custom Python functions"
  },
  {
    type: EDatasourceType.JavaAction,
    card: "function",
    section: "Development",
    iconName: "java",
    label: "Java",
    description: "Custom Java functions"
  },
  {
    type: EDatasourceType.GoAction,
    card: "function",
    section: "Development",
    iconName: "go",
    label: "Go",
    description: "Custom Go functions"
  },
  {
    type: EDatasourceType.RustAction,
    card: "function",
    section: "Development",
    iconName: "rust",
    label: "Rust",
    description: "Custom Rust functions"
  },
  {
    type: EDatasourceType.Mqtt,
    card: "api",
    section: "Communication",
    iconName: "mqtt",
    label: "MQTT",
    description: "Message queuing protocol",
    accessName: "MQTT"
  },
  {
    type: EDatasourceType.Blockly,
    card: "function",
    section: "Development",
    iconName: "puzzle",
    label: "Google Blockly",
    description: "Visual programming blocks",
    accessName: "Google Blockly",
    projectType: EProjectType.Desktop
  },
  {
    type: EDatasourceType.BleService,
    card: "api",
    section: "Communication",
    iconName: "bluetooth",
    label: "Bluetooth LE",
    description: "Bluetooth Low Energy",
    accessName: "Bluetooth",
    projectType: EProjectType.Mobile
  }
];

/** `all` is what the left tree's "+" asks for: every connector, whatever card would normally carry it. */
export type DatasourceCreateCategory = DatasourceCreateCard | "all";

/**
 * The connectors a card offers, minus the ones this project type cannot use.
 *
 * The three cards partition the catalog — every connector belongs to exactly one, which the model test asserts,
 * because a type that belongs to no card becomes unreachable the moment the old three-column screen is deleted.
 * MQTT and Bluetooth sit on the API card rather than with the runtimes: they are connections to something
 * outside the app, while the Function card is code the user writes.
 */
export const getConnectorsFor = (
  category: DatasourceCreateCategory,
  projectType: EProjectType | undefined
): IDatasourceConnector[] =>
  DATASOURCE_CONNECTORS.filter((connector: IDatasourceConnector): boolean => {
    if (connector.projectType !== undefined && connector.projectType !== projectType) return false;
    return category === "all" || connector.card === category;
  });

export interface IDatasourceCardTint {
  background: string;
  color: string;
}

/**
 * Each create card's colour: its own icon tile on the landing page, and the tile of every connector it carries in
 * a picker — so the "+" popover's three columns read as the three cards. Tokens only, never hex: dark mode
 * resolves them. The database values are the create card's own defaults.
 */
export const DATASOURCE_CARD_TINTS: Record<DatasourceCreateCard, IDatasourceCardTint> = {
  database: { background: "var(--blue-05)", color: "var(--accent-fg)" },
  api: { background: "var(--orange-1)", color: "var(--orange-5)" },
  function: { background: "var(--purple-1)", color: "var(--purple-5)" }
};

/** Left to right, the landing page's own order: the "all" picker's columns follow it. */
const CREATE_CARD_ORDER: readonly DatasourceCreateCard[] = ["database", "api", "function"];

export interface IConnectorSection {
  title: string;
  connectors: IDatasourceConnector[];
}

/** One card's connectors, under the headings that card shows them with. */
export interface IConnectorColumn {
  card: DatasourceCreateCard;
  sections: IConnectorSection[];
}

const groupBySection = (connectors: IDatasourceConnector[]): IConnectorSection[] => {
  const sections: IConnectorSection[] = [];
  connectors.forEach((connector: IDatasourceConnector): void => {
    const section: IConnectorSection | undefined = sections.find(
      (candidate: IConnectorSection): boolean => candidate.title === connector.section
    );
    if (section) {
      section.connectors.push(connector);
    } else {
      sections.push({ title: connector.section, connectors: [connector] });
    }
  });
  return sections;
};

/**
 * How a picker lays out a category: a card gets its one column, and `all` gets every card's column side by side —
 * the three cards' own lists next to each other, not a fourth grouping. Columns go by `card`, not by section:
 * MQTT and Bluetooth sit under the API card on the landing page, so they sit in its column here.
 */
export const getConnectorColumns = (
  category: DatasourceCreateCategory,
  projectType: EProjectType | undefined
): IConnectorColumn[] => {
  const cards: readonly DatasourceCreateCard[] = category === "all" ? CREATE_CARD_ORDER : [category];
  return cards
    .map((card: DatasourceCreateCard): IConnectorColumn => ({
      card,
      sections: groupBySection(getConnectorsFor(card, projectType))
    }))
    .filter((column: IConnectorColumn): boolean => column.sections.length > 0);
};

const getColumnConnectors = (column: IConnectorColumn): IDatasourceConnector[] =>
  column.sections.flatMap((section: IConnectorSection): IDatasourceConnector[] => section.connectors);

/**
 * Down each column, then the next. The one order a picker's active row and Enter both index —
 * the order the rows are drawn in, which the catalog's own order is not (Blockly comes after MQTT there).
 */
export const getConnectorReadingOrder = (columns: IConnectorColumn[]): IDatasourceConnector[] =>
  columns.flatMap(getColumnConnectors);

export type ConnectorFocusKey = "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight";

/**
 * Where an arrow key takes the active row (an index into `getConnectorReadingOrder`). Up and down stay inside the
 * column and wrap, as the single-column list always did; left and right cross to the neighbouring column on the
 * same row — its last one when that column is shorter — and stop at the outer edges.
 */
export const moveConnectorFocus = (
  columns: IConnectorColumn[],
  activeIndex: number,
  key: ConnectorFocusKey
): number => {
  const lengths: number[] = columns.map((column: IConnectorColumn): number => getColumnConnectors(column).length);
  const starts: number[] = lengths.map((_length: number, index: number): number =>
    lengths.slice(0, index).reduce((sum: number, length: number): number => sum + length, 0)
  );
  const column: number = starts.findIndex(
    (start: number, index: number): boolean => activeIndex >= start && activeIndex < start + lengths[index]
  );
  if (column === -1) return activeIndex;

  const row: number = activeIndex - starts[column];
  if (key === "ArrowDown" || key === "ArrowUp") {
    const step: number = key === "ArrowDown" ? 1 : -1;
    return starts[column] + ((row + step + lengths[column]) % lengths[column]);
  }

  const target: number = key === "ArrowRight" ? column + 1 : column - 1;
  if (target < 0 || target >= columns.length) return activeIndex;
  return starts[target] + Math.min(row, lengths[target] - 1);
};

export const findConnector = (type: EDatasourceType): IDatasourceConnector | undefined =>
  DATASOURCE_CONNECTORS.find((connector: IDatasourceConnector): boolean => connector.type === type);

//  ────────────────────────────────────────────────────────────────────────────
//  The list
//  ────────────────────────────────────────────────────────────────────────────

export type DatasourceRowKind = "datasource" | "table" | "action";

export interface IDatasourceListDatasourceRow {
  kind: "datasource";
  id: string;
  name: string;
  datasource: IDatasourceDto;
  tableCount: number;
  actionCount: number;
  children: DatasourceListRow[];
}

export interface IDatasourceListTableRow {
  kind: "table";
  id: string;
  name: string;
  table: IDatabaseTableModel;
  datasource: IDatasourceDto;
  /** The table folder this row is drawn in; absent for a table listed under its schema. */
  folderId?: string;
}

export interface IDatasourceListActionRow {
  kind: "action";
  id: string;
  name: string;
  action: IActionDefinitionDto;
  datasource: IDatasourceDto;
  /** The action folder (top level or sub-folder) this row is drawn in; absent for an unfiled action. */
  folderId?: string;
}

/** The two groups a datasource's node opens into, as the Sources tree names them. */
export type DatasourceGroupKind = "actions" | "tables";

/** "Actions" or "Tables" under one datasource. */
export interface IDatasourceListGroupRow {
  kind: "group";
  id: string;
  name: string;
  groupKind: DatasourceGroupKind;
  datasource: IDatasourceDto;
  /** How many actions or tables the rows beneath it hold. */
  count: number;
  children: DatasourceListRow[];
}

/** The unfiled tables of one schema, under a datasource's Tables group. */
export interface IDatasourceListSchemaRow {
  kind: "schema";
  id: string;
  name: string;
  datasource: IDatasourceDto;
  count: number;
  children: DatasourceListRow[];
}

/** An action folder — top level, or one of its sub-folders (one level deep, as the provider supports). */
export interface IDatasourceListActionFolderRow {
  kind: "folder";
  folderKind: "action";
  id: string;
  name: string;
  folder: IActionFolder;
  datasource: IDatasourceDto;
  /** Actions in the folder and in its sub-folders. */
  count: number;
  /** Its own actions FIRST, then its sub-folders — see `getDatasourceRowDragGroupId`. */
  children: DatasourceListRow[];
}

/** A table folder. Table folders do not nest. */
export interface IDatasourceListTableFolderRow {
  kind: "folder";
  folderKind: "table";
  id: string;
  name: string;
  folder: ITableFolder;
  datasource: IDatasourceDto;
  count: number;
  children: DatasourceListRow[];
}

export type DatasourceListFolderRow = IDatasourceListActionFolderRow | IDatasourceListTableFolderRow;

/** The managed database's Session Variables entry; it opens the variables editor. */
export interface IDatasourceListSessionVariablesRow {
  kind: "sessionVariables";
  id: string;
  name: string;
  datasource: IDatasourceDto;
}

export type DatasourceListRow =
  | IDatasourceListDatasourceRow
  | IDatasourceListGroupRow
  | IDatasourceListSchemaRow
  | DatasourceListFolderRow
  | IDatasourceListSessionVariablesRow
  | IDatasourceListTableRow
  | IDatasourceListActionRow;

/** The rows that hold other rows. */
export type DatasourceListContainerRow =
  | IDatasourceListDatasourceRow
  | IDatasourceListGroupRow
  | IDatasourceListSchemaRow
  | DatasourceListFolderRow;

/** Structural stand-in for the provider's private `ITables`. */
export interface IDatasourceSchemaLike {
  tables: IDatabaseTableModel[];
}

export interface IDatasourceRowsInput {
  datasources: IDatasourceDto[];
  schemas: Map<string, IDatasourceSchemaLike>;
  actions: IActionDefinitionDto[];
  /** `project.actionFolders` — each folder names its datasource in `dataSourceType`. */
  actionFolders: IActionFolder[];
  /** `project.databaseTablesFolders`. */
  tableFolders: ITableFolder[];
  searchText: string;
  kindFilter: DatasourceRowKind | "all";
}

/** AI datasources belong to AI Functions, and the provider appends two id-less synthetic ones. */
const HIDDEN_DATASOURCE_TYPES: EDatasourceType[] = [
  EDatasourceType.AIActions,
  EDatasourceType.AIAgent,
  EDatasourceType.VisionAction
];

export const isListableDatasource = (datasource: IDatasourceDto): boolean =>
  !!datasource?.id && !HIDDEN_DATASOURCE_TYPES.includes(datasource.datasourceType);

/** Managed DB is the one with no connection string — the same test the left tree uses. */
export const isManagedDatasource = (datasource: IDatasourceDto): boolean => datasource?.connectionString === "";

const matches = (value: string | undefined, needle: string): boolean =>
  needle.length === 0 || (value ?? "").toLowerCase().indexOf(needle) !== -1;

const ACTIONS_GROUP_NAME: string = "Actions";
const TABLES_GROUP_NAME: string = "Tables";
const SESSION_VARIABLES_NAME: string = "Session Variables";

/** What every level of one build pass needs to know about the filter. */
interface IDatasourceRowBuildContext {
  needle: string;
  isSearching: boolean;
  /** A search or a Kind filter is narrowing the list, so a container left empty by it drops out. */
  isFiltering: boolean;
}

const byName = (a: string, b: string): number => NAME_COLLATOR.compare(a ?? "", b ?? "");

const byActionName = (a: IActionDefinitionDto, b: IActionDefinitionDto): number => byName(a.name, b.name);

const byTableName = (a: IDatabaseTableModel, b: IDatabaseTableModel): number => byName(a.name, b.name);

const byFolderName = (a: IActionFolder | ITableFolder, b: IActionFolder | ITableFolder): number =>
  byName(a.name, b.name);

const isPresent = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

/** A folder's ids, once each: a membership list written twice must not draw its item twice. */
const uniqueIds = (ids: string[] | undefined): string[] => Array.from(new Set<string>(ids ?? []));

const toActionRow = (
  action: IActionDefinitionDto,
  datasource: IDatasourceDto,
  folderId?: string
): IDatasourceListActionRow => ({
  kind: "action",
  id: `action:${action.id}`,
  name: action.name,
  action,
  datasource,
  folderId
});

/**
 * A table row's id — also its draggable id and React key — rests on the identity folders file the
 * table under, which tells same-named tables in two schemas apart where a bare name would not.
 */
const getTableRowId = (table: IDatabaseTableModel, datasource: IDatasourceDto): string =>
  `table:${datasource.id}:${getTableFolderMemberId(table)}`;

const toTableRow = (
  table: IDatabaseTableModel,
  datasource: IDatasourceDto,
  folderId?: string
): IDatasourceListTableRow => ({
  kind: "table",
  id: getTableRowId(table, datasource),
  name: table.name,
  table,
  datasource,
  folderId
});

const countOf = (rows: DatasourceListRow[]): number =>
  rows.reduce((total: number, row: DatasourceListRow): number => {
    if (row.kind === "action" || row.kind === "table") return total + 1;
    // A schema row holds the unfiled tables, so it counts like a folder does.
    if (row.kind === "folder" || row.kind === "schema") return total + row.count;
    return total;
  }, 0);

/**
 * An action folder: its own actions, then — for a top-level folder — its sub-folders, one level
 * deep as the provider writes them. `keepsAll` is true when an ancestor's own name matched the
 * search, which keeps everything beneath it; a folder whose own name matches does the same for its
 * contents.
 */
const buildActionFolderRow = (
  folder: IActionFolder,
  datasource: IDatasourceDto,
  actionsById: Map<string, IActionDefinitionDto>,
  context: IDatasourceRowBuildContext,
  isAncestorMatch: boolean,
  allowsSubFolders: boolean
): IDatasourceListActionFolderRow | null => {
  const isNameMatch: boolean = context.isSearching && matches(folder.name, context.needle);
  const keepsAll: boolean = isAncestorMatch || isNameMatch;

  const own: IDatasourceListActionRow[] = uniqueIds(folder.actionIds)
    .map((actionId: string): IActionDefinitionDto | undefined => actionsById.get(actionId))
    .filter(isPresent)
    .filter((action: IActionDefinitionDto): boolean => keepsAll || matches(action.name, context.needle))
    .sort(byActionName)
    .map((action: IActionDefinitionDto): IDatasourceListActionRow => toActionRow(action, datasource, folder.id));

  const subFolders: IDatasourceListActionFolderRow[] = allowsSubFolders
    ? (folder.children ?? [])
        .slice()
        .sort(byFolderName)
        .map((child: IActionFolder): IDatasourceListActionFolderRow | null =>
          buildActionFolderRow(child, datasource, actionsById, context, keepsAll, false)
        )
        .filter(isPresent)
    : [];

  // Own actions BEFORE sub-folders: a drag group has to stay contiguous, and a sub-folder in the
  // middle would reopen this folder's group further down.
  const children: DatasourceListRow[] = [...own, ...subFolders];
  if (context.isFiltering && !keepsAll && children.length === 0) return null;

  return {
    kind: "folder",
    folderKind: "action",
    id: `action-folder:${folder.id}`,
    name: folder.name,
    folder,
    datasource,
    count: countOf(children),
    children
  };
};

/** Actions: the datasource's folders by name, then the actions no folder holds. */
const buildActionsGroupRow = (
  datasource: IDatasourceDto,
  actions: IActionDefinitionDto[],
  allFolders: IActionFolder[],
  context: IDatasourceRowBuildContext,
  isAncestorMatch: boolean
): IDatasourceListGroupRow | null => {
  const actionsById: Map<string, IActionDefinitionDto> = new Map<string, IActionDefinitionDto>(
    actions.map((action: IActionDefinitionDto): [string, IActionDefinitionDto] => [action.id, action])
  );
  const folders: IActionFolder[] = allFolders
    .filter((folder: IActionFolder): boolean => folder.dataSourceType === datasource.id && !folder.parentFolderId)
    .sort(byFolderName);

  // Everything a folder files is claimed before the search runs, so an action whose folder is
  // filtered out does not resurface as unfiled.
  const filedIds: Set<string> = new Set<string>();
  folders.forEach((folder: IActionFolder): void => {
    [folder, ...(folder.children ?? [])].forEach((entry: IActionFolder): void => {
      (entry.actionIds ?? []).forEach((actionId: string): void => {
        filedIds.add(actionId);
      });
    });
  });

  const folderRows: IDatasourceListActionFolderRow[] = folders
    .map((folder: IActionFolder): IDatasourceListActionFolderRow | null =>
      buildActionFolderRow(folder, datasource, actionsById, context, isAncestorMatch, true)
    )
    .filter(isPresent);
  const unfiledRows: IDatasourceListActionRow[] = actions
    .filter((action: IActionDefinitionDto): boolean => !filedIds.has(action.id))
    .filter((action: IActionDefinitionDto): boolean => isAncestorMatch || matches(action.name, context.needle))
    .sort(byActionName)
    .map((action: IActionDefinitionDto): IDatasourceListActionRow => toActionRow(action, datasource));

  const children: DatasourceListRow[] = [...folderRows, ...unfiledRows];
  if (context.isFiltering && !isAncestorMatch && children.length === 0) return null;

  return {
    kind: "group",
    groupKind: "actions",
    id: `group:actions:${datasource.id}`,
    name: ACTIONS_GROUP_NAME,
    datasource,
    count: countOf(children),
    children
  };
};

const buildSchemaRow = (
  schemaName: string,
  tables: IDatabaseTableModel[],
  datasource: IDatasourceDto,
  context: IDatasourceRowBuildContext,
  isAncestorMatch: boolean
): IDatasourceListSchemaRow | null => {
  const keepsAll: boolean = isAncestorMatch || (context.isSearching && matches(schemaName, context.needle));
  const children: IDatasourceListTableRow[] = tables
    .filter((table: IDatabaseTableModel): boolean => keepsAll || matches(table.name, context.needle))
    .sort(byTableName)
    .map((table: IDatabaseTableModel): IDatasourceListTableRow => toTableRow(table, datasource));
  // A schema exists only through its tables; with none left there is nothing to head.
  if (children.length === 0) return null;
  return {
    kind: "schema",
    id: `schema:${datasource.id}:${schemaName}`,
    name: schemaName,
    datasource,
    count: children.length,
    children
  };
};

const buildTableFolderRow = (
  folder: ITableFolder,
  datasource: IDatasourceDto,
  tablesByMemberId: Map<string, IDatabaseTableModel>,
  context: IDatasourceRowBuildContext,
  isAncestorMatch: boolean
): IDatasourceListTableFolderRow | null => {
  const isNameMatch: boolean = context.isSearching && matches(folder.name, context.needle);
  const keepsAll: boolean = isAncestorMatch || isNameMatch;
  const children: IDatasourceListTableRow[] = uniqueIds(folder.tableIds)
    .map((memberId: string): IDatabaseTableModel | undefined => tablesByMemberId.get(memberId))
    .filter(isPresent)
    .filter((table: IDatabaseTableModel): boolean => keepsAll || matches(table.name, context.needle))
    .sort(byTableName)
    .map((table: IDatabaseTableModel): IDatasourceListTableRow => toTableRow(table, datasource, folder.id));
  if (context.isFiltering && !keepsAll && children.length === 0) return null;
  return {
    kind: "folder",
    folderKind: "table",
    id: `table-folder:${folder.id}`,
    name: folder.name,
    folder,
    datasource,
    count: children.length,
    children
  };
};

/** Tables: the unfiled ones under a header per schema, then the table folders by name. */
const buildTablesGroupRow = (
  datasource: IDatasourceDto,
  tables: IDatabaseTableModel[],
  allFolders: ITableFolder[],
  context: IDatasourceRowBuildContext,
  isAncestorMatch: boolean
): IDatasourceListGroupRow | null => {
  const folders: ITableFolder[] = allFolders
    .filter((folder: ITableFolder): boolean => folder.dataSourceType === datasource.id)
    .sort(byFolderName);
  const filedIds: Set<string> = new Set<string>(
    folders.flatMap((folder: ITableFolder): string[] => folder.tableIds ?? [])
  );
  const tablesByMemberId: Map<string, IDatabaseTableModel> = new Map<string, IDatabaseTableModel>(
    tables.map((table: IDatabaseTableModel): [string, IDatabaseTableModel] => [getTableFolderMemberId(table), table])
  );

  const unfiled: IDatabaseTableModel[] = tables.filter(
    (table: IDatabaseTableModel): boolean => !filedIds.has(getTableFolderMemberId(table))
  );
  const schemaRows: IDatasourceListSchemaRow[] = Array.from(groupTablesBySchema(unfiled).entries())
    .sort((a: [string, IDatabaseTableModel[]], b: [string, IDatabaseTableModel[]]): number => byName(a[0], b[0]))
    .map(([schemaName, schemaTables]: [string, IDatabaseTableModel[]]): IDatasourceListSchemaRow | null =>
      buildSchemaRow(schemaName, schemaTables, datasource, context, isAncestorMatch)
    )
    .filter(isPresent);
  const folderRows: IDatasourceListTableFolderRow[] = folders
    .map((folder: ITableFolder): IDatasourceListTableFolderRow | null =>
      buildTableFolderRow(folder, datasource, tablesByMemberId, context, isAncestorMatch)
    )
    .filter(isPresent);

  const children: DatasourceListRow[] = [...schemaRows, ...folderRows];
  if (context.isFiltering && !isAncestorMatch && children.length === 0) return null;

  return {
    kind: "group",
    groupKind: "tables",
    id: `group:tables:${datasource.id}`,
    name: TABLES_GROUP_NAME,
    datasource,
    count: countOf(children),
    children
  };
};

/**
 * One datasource per root row, holding what the Sources tree holds under it: an Actions group
 * (folders, each with its own actions before its sub-folders, then the unfiled actions), a Tables
 * group where the type has tables (unfiled tables under a header per schema, then table folders),
 * and the managed database's Session Variables.
 *
 * A search keeps a row whose OWN name matches — a datasource, a folder, a schema — with everything
 * beneath it, and otherwise keeps only the matching rows together with the path down to them. A
 * container left empty by a search or a Kind filter drops out; unfiltered, an empty group or
 * folder stays, since it is where the first item is created or dropped.
 */
export const buildDatasourceRows = (input: IDatasourceRowsInput): IDatasourceListDatasourceRow[] => {
  const needle: string = input.searchText.trim().toLowerCase();
  const context: IDatasourceRowBuildContext = {
    needle,
    isSearching: needle.length > 0,
    isFiltering: needle.length > 0 || input.kindFilter !== "all"
  };
  const wantsTables: boolean = input.kindFilter === "all" || input.kindFilter === "table";
  const wantsActions: boolean = input.kindFilter === "all" || input.kindFilter === "action";
  const rows: IDatasourceListDatasourceRow[] = [];

  input.datasources.filter(isListableDatasource).forEach((datasource: IDatasourceDto): void => {
    const tables: IDatabaseTableModel[] = (input.schemas.get(datasource.id)?.tables ?? []).filter(
      (table: IDatabaseTableModel): boolean => table.dropped !== true
    );
    const actions: IActionDefinitionDto[] = input.actions.filter(
      (action: IActionDefinitionDto): boolean => action.kDatasource_Id === datasource.id
    );

    const displayName: string = getDatasourceDisplayName(datasource);
    const isDatasourceMatch: boolean = matches(displayName, needle);
    const isAncestorMatch: boolean = context.isSearching && isDatasourceMatch;

    const children: DatasourceListRow[] = [];
    if (input.kindFilter !== "datasource") {
      if (wantsActions) {
        const actionsGroup: IDatasourceListGroupRow | null = buildActionsGroupRow(
          datasource,
          actions,
          input.actionFolders ?? [],
          context,
          isAncestorMatch
        );
        if (actionsGroup) children.push(actionsGroup);
      }
      if (wantsTables && hasTablesGroup(datasource)) {
        const tablesGroup: IDatasourceListGroupRow | null = buildTablesGroupRow(
          datasource,
          tables,
          input.tableFolders ?? [],
          context,
          isAncestorMatch
        );
        if (tablesGroup) children.push(tablesGroup);
      }
      const showsSessionVariables: boolean =
        input.kindFilter === "all" &&
        hasSessionVariablesRow(datasource) &&
        (isAncestorMatch || matches(SESSION_VARIABLES_NAME, needle));
      if (showsSessionVariables) {
        children.push({
          kind: "sessionVariables",
          id: `session-variables:${datasource.id}`,
          name: SESSION_VARIABLES_NAME,
          datasource
        });
      }
    }

    const keepsDatasource: boolean =
      ((input.kindFilter === "all" || input.kindFilter === "datasource") && isDatasourceMatch) || children.length > 0;
    if (!keepsDatasource) return;

    rows.push({
      kind: "datasource",
      id: `datasource:${datasource.id}`,
      name: displayName,
      datasource,
      tableCount: tables.length,
      actionCount: actions.length,
      children
    });
  });

  return rows.sort((a: IDatasourceListDatasourceRow, b: IDatasourceListDatasourceRow): number => {
    // Managed DB first, the way the left tree sorts it, then by name.
    const aManaged: boolean = isManagedDatasource(a.datasource);
    const bManaged: boolean = isManagedDatasource(b.datasource);
    if (aManaged !== bManaged) return aManaged ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
  });
};

//  ────────────────────────────────────────────────────────────────────────────
//  Walking the tree
//  ────────────────────────────────────────────────────────────────────────────

/** A container row's children; undefined for a leaf. What the table's `getSubRows` answers. */
export const getDatasourceListChildren = (row: DatasourceListRow): DatasourceListRow[] | undefined => {
  if (row.kind === "datasource" || row.kind === "group" || row.kind === "schema" || row.kind === "folder") {
    return row.children;
  }
  return undefined;
};

/** Every row, depth first, in the order the table draws a fully open tree. */
export const flattenDatasourceRows = (rows: DatasourceListRow[]): DatasourceListRow[] =>
  rows.flatMap((row: DatasourceListRow): DatasourceListRow[] => [
    row,
    ...flattenDatasourceRows(getDatasourceListChildren(row) ?? [])
  ]);

/** The first row the predicate accepts, with every ancestor before it (root first); null if none. */
export const findDatasourceRowPath = (
  rows: DatasourceListRow[],
  predicate: (row: DatasourceListRow) => boolean
): DatasourceListRow[] | null => {
  for (const row of rows) {
    if (predicate(row)) return [row];
    const inner: DatasourceListRow[] | null = findDatasourceRowPath(getDatasourceListChildren(row) ?? [], predicate);
    if (inner) return [row, ...inner];
  }
  return null;
};

//  ────────────────────────────────────────────────────────────────────────────
//  Moving rows between folders
//  ────────────────────────────────────────────────────────────────────────────

/**
 * The drop list a row belongs to. `KuikaTable` turns every run of equal ids into one droppable, so
 * each id must be contiguous in draw order: a folder header shares its id with its own rows (which
 * come before its sub-folders), all of a datasource's schema headers and unfiled tables share one,
 * and every other row is a group of its own. Every id is scoped to its datasource or folder.
 */
export const getDatasourceRowDragGroupId = (row: DatasourceListRow): string => {
  if (row.kind === "action") {
    return row.folderId ? `action-folder:${row.folderId}` : `unfiled-actions:${row.datasource.id}`;
  }
  if (row.kind === "table") {
    return row.folderId ? `table-folder:${row.folderId}` : `unfiled-tables:${row.datasource.id}`;
  }
  if (row.kind === "schema") return `unfiled-tables:${row.datasource.id}`;
  return row.id;
};

/** What a row can be dragged into: actions into action folders, tables into table folders. */
export type DatasourceMovableKind = "action" | "table";

/** Where a drop on a drag group lands. `folderId` null means "out of every folder". */
export interface IDatasourceDropTarget {
  accepts: DatasourceMovableKind;
  datasourceId: string;
  folderId: string | null;
}

const getDropTarget = (row: DatasourceListRow): IDatasourceDropTarget | null => {
  const datasourceId: string = row.datasource.id;
  if (row.kind === "group") {
    return { accepts: row.groupKind === "actions" ? "action" : "table", datasourceId, folderId: null };
  }
  if (row.kind === "schema") return { accepts: "table", datasourceId, folderId: null };
  if (row.kind === "folder") return { accepts: row.folderKind, datasourceId, folderId: row.folder.id };
  if (row.kind === "action" || row.kind === "table") {
    return { accepts: row.kind, datasourceId, folderId: row.folderId ?? null };
  }
  return null;
};

/** Every drag group's landing place, keyed by the group id a drop reports. */
export const indexDatasourceDropTargets = (rows: DatasourceListRow[]): Map<string, IDatasourceDropTarget> => {
  const targets: Map<string, IDatasourceDropTarget> = new Map<string, IDatasourceDropTarget>();
  flattenDatasourceRows(rows).forEach((row: DatasourceListRow): void => {
    const target: IDatasourceDropTarget | null = getDropTarget(row);
    if (target) targets.set(getDatasourceRowDragGroupId(row), target);
  });
  return targets;
};

export interface IDatasourceActionMove {
  kind: "action";
  actionId: string;
  fromFolderId: string | null;
  toFolderId: string | null;
}

export interface IDatasourceTableMove {
  kind: "table";
  /** The id `ITableFolder.tableIds` holds — see `getTableFolderMemberId`. */
  memberId: string;
  fromFolderId: string | null;
  toFolderId: string | null;
}

export type DatasourceRowMove = IDatasourceActionMove | IDatasourceTableMove;

/**
 * What a drop means, or null when it means nothing. An action goes only into an action folder of
 * its own datasource and a table only into a table folder of its own; landing on a group, a schema
 * or among unfiled rows takes it out of its folder. Let go clear of every group, a filed row leaves
 * its folder — the AI Functions list reads that gesture the same way, since with everything filed
 * there is no unfiled row left to aim at.
 */
export const resolveDatasourceRowMove = (
  dragged: DatasourceListRow,
  target: IDatasourceDropTarget | null
): DatasourceRowMove | null => {
  if (dragged.kind !== "action" && dragged.kind !== "table") return null;
  const fromFolderId: string | null = dragged.folderId ?? null;
  let toFolderId: string | null = null;
  if (target) {
    if (target.accepts !== dragged.kind || target.datasourceId !== dragged.datasource.id) return null;
    toFolderId = target.folderId;
  }
  if (toFolderId === fromFolderId) return null;
  if (dragged.kind === "action") {
    return { kind: "action", actionId: dragged.action.id, fromFolderId, toFolderId };
  }
  return { kind: "table", memberId: getTableFolderMemberId(dragged.table), fromFolderId, toFolderId };
};

/** What deciding whether a row can be picked up needs from the project and the user's role. */
export interface IDatasourceRowDragContext {
  /** `project.actionFolders`. */
  actionFolders: IActionFolder[];
  /** `project.databaseTablesFolders`. */
  tableFolders: ITableFolder[];
  canManageActions: boolean;
  canManageTables: boolean;
}

/** Whether a folder of this kind hangs off the datasource: somewhere for its rows to go. */
export const hasDatasourceFolderFor = (
  kind: DatasourceMovableKind,
  datasourceId: string,
  context: IDatasourceRowDragContext
): boolean => {
  const folders: (IActionFolder | ITableFolder)[] = kind === "action" ? context.actionFolders : context.tableFolders;
  return folders.some((folder: IActionFolder | ITableFolder): boolean => folder.dataSourceType === datasourceId);
};

/**
 * Only actions and tables are cargo, only for the role that manages them, and — as on the Sources
 * tree — only where their datasource has a folder of their kind to go into. A table folder cannot
 * be created (the project does not save them), so in practice no table row is picked up; the rule
 * stays on the folders there are rather than on the kind, so a grip never appears with nowhere to
 * drop.
 */
export const isDatasourceRowDraggable = (row: DatasourceListRow, context: IDatasourceRowDragContext): boolean => {
  if (row.kind !== "action" && row.kind !== "table") return false;
  if (!hasDatasourceFolderFor(row.kind, row.datasource.id, context)) return false;
  return row.kind === "action" ? context.canManageActions : context.canManageTables;
};

/**
 * The name the tree shows: the managed database is "Managed DB" everywhere in the designer,
 * not the raw row name (which is the domain's, e.g. `acmeDatasource`).
 */
export const getDatasourceDisplayName = (datasource: IDatasourceDto): string =>
  isManagedDatasource(datasource) ? "Managed DB" : datasource.name;

/** Label for the datasource row's Type column — the connector's own name where there is one. */
export const getDatasourceTypeLabel = (datasource: IDatasourceDto): string => {
  if (isManagedDatasource(datasource)) return "Managed database";
  const connector: IDatasourceConnector | undefined = findConnector(datasource.datasourceType);
  if (connector) return connector.label;
  if (datasource.datasourceType === EDatasourceType.CustomDb) return "MS SQL Server";
  if (datasource.datasourceType === EDatasourceType.ManagedDbOracle) return "Managed database (Oracle)";
  if (datasource.datasourceType === EDatasourceType.ManagedDbPostgre) return "Managed database (PostgreSQL)";
  return "Datasource";
};

/** Glyph for a datasource row; falls back to the generic database icon. */
export const getDatasourceIconName = (datasource: IDatasourceDto): string => {
  if (isManagedDatasource(datasource)) return "database";
  return findConnector(datasource.datasourceType)?.iconName ?? "database";
};

//  ────────────────────────────────────────────────────────────────────────────
//  Where a new table or action can go
//  ────────────────────────────────────────────────────────────────────────────

/**
 * A table can only be created on a managed database — the same `connectionString === ""` test the left tree
 * gates its "New Table" item on. A connected SQL server's schema belongs to that server, not to us.
 */
export const isTableCapableDatasource = (datasource: IDatasourceDto): boolean =>
  isListableDatasource(datasource) && isManagedDatasource(datasource);

/**
 * An action can be created wherever the SQL action editor will actually mount — the six types in
 * `isSqlActionDatasourceType`. The left tree offers "New Action" on a wider set (REST endpoints, C# files and the
 * rest reach their own editors by other routes), but this sheet leads to `CreateAction`, and offering a type that
 * component does not render would open an empty editor.
 */
export const isActionCapableDatasource = (datasource: IDatasourceDto): boolean =>
  isListableDatasource(datasource) && isSqlActionDatasourceType(datasource.datasourceType);

/** The project's managed database, which both sheets default to. */
export const getManagedDatasource = (datasources: IDatasourceDto[]): IDatasourceDto | undefined =>
  (datasources ?? []).find((datasource: IDatasourceDto): boolean => isTableCapableDatasource(datasource));

//  ────────────────────────────────────────────────────────────────────────────
//  Centre-surface state (owned by the datasource provider)
//  ────────────────────────────────────────────────────────────────────────────

/**
 * The landing or the ER diagram. Which of the landing's tabs shows is the landing's own derived
 * state: it depends on the modification feed the landing fetches, which the provider never sees.
 */
export type DatasourcesCentreView = "list" | "diagram";

/** Which connector sheet a surface outside the landing is asking for. */
export type DatasourceCreateIntent = DatasourceCreateCategory;

//  ────────────────────────────────────────────────────────────────────────────
//  Recently modified
//  ────────────────────────────────────────────────────────────────────────────

/** The kinds the list nests. Only two of them can ever be dated — see `datasourceRecentsFromFeed`. */
export type DatasourceRecentKind = DatasourceRowKind;

/** One entity the modification feed dates, held by id; `buildDatasourceRecentRows` resolves the rest. */
export interface IDatasourceRecent {
  kind: DatasourceRecentKind;
  /** Datasource id, action id, or a table's `getTableFolderMemberId` — the id the list rows are keyed by. */
  entityKey: string;
  /** The datasource the entity belongs to; equal to `entityKey` for a datasource. */
  datasourceId: string;
  /** Epoch ms of the entity's last write, as the modification feed reports it. */
  modifiedAt: number;
}

/** A datasource or action ahead of dating, carrying the name the tie-break sorts on. */
interface IDatasourceRecentCandidate {
  kind: Exclude<DatasourceRecentKind, "table">;
  entityKey: string;
  datasourceId: string;
  name: string;
}

/**
 * The datasources and actions the entity-modification feed dates inside the window, newest first,
 * ties broken by the name the tab shows.
 *
 * Never a table. A managed database's table schemas are read live from the customer's database and
 * carry no timestamp anywhere on the platform, so there is nothing to date one by — and borrowing
 * its datasource's date would claim a change the table may never have had.
 *
 * Only what the landing can draw is emitted, so a datasource the list hides (AI Functions' own, the
 * provider's id-less synthetic entries) drops out together with its actions. Emitting them would
 * count towards the landing's "is anything recent" answer while rendering as no row at all, and the
 * page would open on a tab claiming nothing had changed.
 *
 * Both kinds go through one `selectRecentlyModified` call, so the window, the order and the
 * tie-break are the ones every other landing uses rather than a second copy of them.
 */
export const datasourceRecentsFromFeed = (
  datasources: IDatasourceDto[],
  actions: IActionDefinitionDto[],
  index: ModificationIndex,
  now: number
): IDatasourceRecent[] => {
  const listable: IDatasourceDto[] = datasources.filter(isListableDatasource);
  const listableIds: Set<string> = new Set<string>(listable.map((datasource: IDatasourceDto): string => datasource.id));

  const candidates: IDatasourceRecentCandidate[] = [
    ...listable.map((datasource: IDatasourceDto): IDatasourceRecentCandidate => ({
      kind: "datasource",
      entityKey: datasource.id,
      datasourceId: datasource.id,
      name: getDatasourceDisplayName(datasource)
    })),
    ...actions
      .filter((action: IActionDefinitionDto): boolean => listableIds.has(action.kDatasource_Id))
      .map((action: IActionDefinitionDto): IDatasourceRecentCandidate => ({
        kind: "action",
        entityKey: action.id,
        datasourceId: action.kDatasource_Id,
        name: action.name ?? ""
      }))
  ];

  const recent: IDatasourceRecentCandidate[] = selectRecentlyModified<IDatasourceRecentCandidate>({
    items: candidates,
    index,
    getId: (candidate: IDatasourceRecentCandidate): string => candidate.entityKey,
    getName: (candidate: IDatasourceRecentCandidate): string => candidate.name,
    now
  });

  return recent.flatMap((candidate: IDatasourceRecentCandidate): IDatasourceRecent[] => {
    const entry: IRecentlyModifiedEntry | undefined = lookupModification(index, candidate.entityKey);
    // Unreachable: the selection kept only dated candidates. The guard is for the type checker.
    if (!entry) return [];
    return [
      {
        kind: candidate.kind,
        entityKey: candidate.entityKey,
        datasourceId: candidate.datasourceId,
        modifiedAt: entry.modifiedAt
      }
    ];
  });
};

/** A feed-dated entity, resolved against the live model. */
export interface IDatasourceRecentRow {
  row: DatasourceListRow;
  modifiedAt: number;
}

export interface IDatasourceRecentRowsInput {
  recents: IDatasourceRecent[];
  datasources: IDatasourceDto[];
  schemas: Map<string, IDatasourceSchemaLike>;
  actions: IActionDefinitionDto[];
  searchText: string;
  kindFilter: DatasourceRowKind | "all";
}

/**
 * Turns the dated ids into the row shapes the list already renders. It keeps the order it is given
 * rather than sorting again, so the tab's order is `datasourceRecentsFromFeed`'s alone.
 *
 * Resolving against the live model is what keeps the list honest: a renamed entity reads with
 * its new name, and one that was deleted — or whose datasource was — drops out instead of
 * offering a dead row.
 */
export const buildDatasourceRecentRows = (input: IDatasourceRecentRowsInput): IDatasourceRecentRow[] => {
  const needle: string = input.searchText.trim().toLowerCase();
  const rows: IDatasourceRecentRow[] = [];

  input.recents.forEach((recent: IDatasourceRecent): void => {
    if (input.kindFilter !== "all" && input.kindFilter !== recent.kind) return;

    const datasource: IDatasourceDto | undefined = input.datasources.find(
      (candidate: IDatasourceDto): boolean => candidate.id === recent.datasourceId
    );
    if (!datasource || !isListableDatasource(datasource)) return;

    if (recent.kind === "datasource") {
      const name: string = getDatasourceDisplayName(datasource);
      if (!matches(name, needle)) return;
      rows.push({
        modifiedAt: recent.modifiedAt,
        row: {
          kind: "datasource",
          id: `datasource:${datasource.id}`,
          name,
          datasource,
          tableCount: (input.schemas.get(datasource.id)?.tables ?? []).length,
          actionCount: input.actions.filter(
            (action: IActionDefinitionDto): boolean => action.kDatasource_Id === datasource.id
          ).length,
          children: []
        }
      });
      return;
    }

    if (recent.kind === "table") {
      const table: IDatabaseTableModel | undefined = (input.schemas.get(datasource.id)?.tables ?? []).find(
        (candidate: IDatabaseTableModel): boolean =>
          candidate.dropped !== true && getTableFolderMemberId(candidate) === recent.entityKey
      );
      if (!table || !matches(table.name, needle)) return;
      rows.push({
        modifiedAt: recent.modifiedAt,
        row: {
          kind: "table",
          id: getTableRowId(table, datasource),
          name: table.name,
          table,
          datasource
        }
      });
      return;
    }

    const action: IActionDefinitionDto | undefined = input.actions.find(
      (candidate: IActionDefinitionDto): boolean => candidate.id === recent.entityKey
    );
    if (!action || !matches(action.name, needle)) return;
    rows.push({
      modifiedAt: recent.modifiedAt,
      row: { kind: "action", id: `action:${action.id}`, name: action.name, action, datasource }
    });
  });

  return rows;
};
